import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate, NavLink } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Loader from '../common/admin/Loader';
import SignIn from '../pages/admin/Authentication/SignIn';
import Dashboard from '../pages/admin/Dashboard';
import '../styles/admin/admin.css';
import '../styles/admin/satoshi.css';
import routes from './admin/'
import { AuthProvider, useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { UserRole } from '../config/rbac';
import posthog from 'posthog-js';

const DefaultLayout = lazy(() => import('../layout/admin/DefaultLayout'));

const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { hasRole } = usePermissions();
  const location = useLocation();

  // While the authentication status is being checked, show a loader.
  // This prevents the "flash" of a protected page or a redirect.
  if (isLoading) {
    return <Loader />;
  }

  // Once loading is complete, if the user is not authenticated, redirect to sign-in.
  // This is the primary guard against unauthorized access.
  if (!isAuthenticated) {
    if (posthog.__loaded) {
      posthog.capture('protected_route_access_denied', {
        reason: 'not_authenticated',
        target_path: location.pathname,
      });
    }
    return <Navigate to="/admin/signin" state={{ from: location }} replace />;
  }

  const userHasRequiredRole = requiredRoles.length === 0 || requiredRoles.some(role => hasRole(role));

  if (!userHasRequiredRole) {
    if (posthog.__loaded) {
      posthog.capture('protected_route_access_denied', {
        reason: 'missing_required_roles',
        target_path: location.pathname,
        required_roles: requiredRoles,
        user_roles: user?.groups,
      });
    }
    return <Navigate to="/admin/unauthorized" replace />;
  }

  return children;
};

const UnauthorizedPage = () => {
  // ... (content is unchanged)
  return (
    <div className="flex h-screen items-center justify-center bg-boxdark p-6 text-center">
      <div>
        <h1 className="mb-4 text-4xl font-bold text-danger">Access Denied</h1>
        <p className="mb-6 text-xl text-bodydark">
          You don't have permission to access this page.
        </p>
        <NavLink
          to="/admin"
          className="rounded-md bg-primary py-3 px-6 text-base font-medium text-white hover:bg-opacity-90"
        >
          Back to Dashboard
        </NavLink>
      </div>
    </div>
  )
};

function AdminRoutesContent() {
  // --- UPDATED --- Use authStatus to control rendering
  const { authStatus } = useAuth();

  // --- NEW --- If the auth status is pending, show a full-screen loader.
  // This prevents the sign-in page from ever showing if a user is already logged in.
  if (authStatus === 'PENDING') {
    return <Loader />;
  }
  
  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        containerClassName="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-[9999]"
        toastOptions={{
          duration: 5000,
        }}
      />
      <Routes>
        <Route path="signin" element={<SignIn />} />
        <Route path="unauthorized" element={<UnauthorizedPage />} />

        <Route
          path="/*" // Match all other admin routes
          element={
            <ProtectedRoute>
              <DefaultLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />

          {routes.map((routeItem, index) => {
            const { path, component: Component, requiredRoles = [] } = routeItem;
            return (
              <Route
                key={index}
                path={path}
                element={
                  <ProtectedRoute requiredRoles={requiredRoles}>
                    <Suspense fallback={<Loader />}>
                      <Component />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            );
          })}
          {/* Fallback for any unmatched nested admin routes */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default function AdminRoutes() {
  return (
    <AuthProvider>
      <AdminRoutesContent />
    </AuthProvider>
  );
}